import * as signalR from "@microsoft/signalr";
import Vue from "vue";
import router from "@/router";
import { Message } from "element-ui";
import i18n from "@/i18n/i18n.js";
import { handleNavColor } from "@/plugins/handleNavColor";
import { ClientChat } from "@/api/clientChat.js";
import dayjs from "dayjs";
const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

const notification = document.getElementById("notification");

const GET_NEW_MESSAGE = i18n.t("LAYOUT.GET_NEW_MESSAGE");
const GET_NEW_LINK = i18n.t("LAYOUT.GET_NEW_LINK");
const GET_NEW_FILE = i18n.t("LAYOUT.GET_NEW_FILE");
const MSG_ONLOGOUT = i18n.t("LAYOUT.MSG_ONLOGOUT");
const MSG_ONCLOSE = i18n.t("LAYOUT.MSG_ONCLOSE");
const CLIENT_HANGUP = i18n.t("VIDEO.LAYOUT.CLIENT_HANGUP");
const CLIENT_REFUSES = i18n.t("VIDEO.LAYOUT.CLIENT_REFUSES");

let timer;

// AP-12932 失敗訊息佇列的持久化與自動補送
const OFFLINE_MESSAGES_KEY = "AC_offlineMessages";
// Exponential Backoff + Jitter，避免瞬斷後大量連線同時重送(Thundering Herd)
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000, 30000];
const RETRY_JITTERS = [500, 1000, 2000, 2000, 3000, 5000];
let flushing = false;
let flushTimer;

const loadOfflineMessages = () => {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_MESSAGES_KEY)) || [];
  } catch (error) {
    return [];
  }
};

const saveOfflineMessages = (messages) => {
  try {
    localStorage.setItem(OFFLINE_MESSAGES_KEY, JSON.stringify(messages));
  } catch (error) {
    console.log("saveOfflineMessages failed", error);
  }
};

const retryDelay = (attempt) => {
  const i = Math.min(attempt, RETRY_DELAYS.length - 1);
  return RETRY_DELAYS[i] + (Math.random() * 2 - 1) * RETRY_JITTERS[i];
};

const state = {
  internetState: "online",
  hasNewMsg: false,
  loading: false,
  connection: null,
  hubUrl: process.env.VUE_APP_BASEURL + "/clientChat",
  clientChatRoomData: [],
  messageCenter: null,
  offlineMessages: loadOfflineMessages(),
  replyMessage: null,
};

const actions = {
  stopConnection({ commit }) {
    state.connection.stop().then(() => {
      console.log("connection stop.");
    });
    commit("cleanConnection");
  },
  initConnection({ state, rootState, dispatch }, payload) {
    let { isService } = rootState.account.AC_userdata;
    if (isService) {
      // console.log("1. 初始化 clientChat 連線(new signalR)");
      state.connection = new signalR.HubConnectionBuilder()
        .withUrl(state.hubUrl, {
          accessTokenFactory: () => localStorage.getItem("AC_token"),
        })
        .build();
      // state.connection.serverTimeoutInMilliseconds = 24e4;
      // state.connection.keepAliveIntervalInMilliseconds = 12e4
      dispatch("startConnection");
    } else {
      // console.log("user isService: false");
    }
  },
  async startConnection({ state, dispatch }) {
    if (state.connection) {
      try {
        await state.connection.start();
        dispatch("onReady");
        // console.log("2. clientChat connection onReady");
        if (timer) {
          // Message({
          //   message: "HubConnection reconnected successfully",
          //   center: true,
          //   iconClass: "el-icon-circle-check",
          //   customClass: "msg msg-success",
          //   showClose: true,
          //   duration: 0,
          // });
          handleNavColor(true);
          clearTimeout(timer);
        }
      } catch (error) {
        timer = setTimeout(function () {
          dispatch("startConnection");
        }, 10000);
      }
    } else {
      dispatch("initConnection");
    }
  },

  // on listen
  onReady({ rootState, state, dispatch, commit }) {
    state.connection.onclose(async () => {
      commit("cleanConnection", false); // false = 清除connection 不清除 room
      // 需cleanConnection , 否則 LoadClientChatRoom / NewMessage 會 on 兩次
      // 登入狀態下的中斷才需要重連
      let isLogin = rootState.account.AC_token;
      if (isLogin) {
        console.log(MSG_ONCLOSE);
        // ACC 錯誤訊息不顯示，僅顯示於 console.log
        // Message({
        //   message: MSG_ONCLOSE,
        //   center: true,
        //   iconClass: "el-icon-error",
        //   customClass: "msg msg-error",
        //   showClose: true,
        //   duration: 5000,
        // });
        handleNavColor(false);
        await dispatch("startConnection");
        return;
      }
    });
    // onError
    state.connection.on("onError", function (message) {
      console.log("connection onError:", message);
      // ACC 錯誤訊息不顯示，僅顯示於 console.log
      // Message({
      //   message: message,
      //   center: true,
      //   iconClass: "el-icon-circle-close",
      //   customClass: "msg msg-error",
      //   duration: 5000,
      //   showClose: true,
      // });
    });
    // LoadClientChatRoom
    state.connection.on("LoadClientChatRoom", function (data) {
      // console.log("LoadClientChatRoom", data);
      data.forEach((r) => {
        r.heartBeatTime = new Date().getTime();
        let timer;
        if (r.leftPickUpBufferTime > 0) {
          // console.log('handlePickUp');
          timer = setInterval(function () {
            if (r.leftPickUpBufferTime > 0) {
              commit("handlePickUp", r.id);
            } else {
              clearInterval(timer);
            }
          }, 1000);
        }
      });
      commit("setClientChatRoom", data);
      // AP-12932 房間載入完成才補送，否則 checkIdle 取不到 room
      dispatch("flushOfflineMessages");
    });
    // JoinChatRoom
    state.connection.on("JoinChatRoom", function (data) {
      // console.log("收到加入ChatRomm訊息 ChatRoom Id :", data.clientChatRoom);
      notification.pause();
      notification.currentTime = 0;
      notification.play();
      commit("addNewClientChatRoom", data.clientChatRoom);
    });
    // on temp message
    state.connection.on("newCustomerTempMessage", function (data) {
      // console.log("客戶正在輸入訊息：", data);
      // let status = state.connection.__ == "Connected";
      commit("setTempMessage", data);
    });
    // newMessage
    state.connection.on("NewMessage", function (data) {
      let type = data.type;
      let hasFocus = document.hasFocus();
      let roomId = data.roomId.toString();
      let currentRoomId = router.currentRoute.params.id;
      let roomBody = document.getElementById(roomId);
      let isScrollBottom = true;
      // AP-3752 isNowAgent
      let { isNowAgent } = state.clientChatRoomData.find((el) => {
        return el.id === roomId;
      });
      if (isNowAgent) {
        if (roomBody) {
          // 若正在當下的 room 才能抓到 dom
          isScrollBottom =
            roomBody.clientHeight + roomBody.scrollTop == roomBody.scrollHeight;
        }
        if (type == "AgentIsTyping") {
          // Agent 正在輸入
          return;
        }
        if (type == "VideoCall" || type == "QRCode") {
          // 存下當前視訊錄影的 messageId 供後端合併錄影blob使用
          dispatch("webrtc/setMessageId", data.id, { root: true });
        }
        if (type == "CustomerStatus") {
          commit("setClientChatRoomStatus", data);
        }
        if (type !== "System" && type !== "CustomerStatus") {
          // 提醒 : System 沒有回傳 user , system 無法判斷 username
          // CustomerStatus 不做提示音 , 只做燈號顯示
          let isMe =
            rootState.account.AC_userdata.username == data.user.username;
          if (!isMe) {
            // 播放聲音訊息
            notification.pause();
            notification.currentTime = 0;
            notification.play();
          }
        }
        if (roomId == currentRoomId) {
          let readMessageData = { ClientChatRoomId: roomId };
          // console.log('您剛好在同房間內收到對方訊息 ReadMessage');
          dispatch("readMessage", readMessageData);
          data.isRead = true;
        }
        if (!hasFocus && type !== "CustomerStatus") {
          // 背景狀態下 (hasFocus) 跳出系統預設提示
          let generateMessage = function (data) {
            let msg;
            switch (type) {
              case "Text": //訊息 or 連結
                if (data.linkInfos.length) {
                  msg = GET_NEW_LINK;
                } else {
                  msg = GET_NEW_MESSAGE;
                }
                break;
              case "System": // 系統
                msg = data.content;
                break;
              case "Files": // 圖片 or 檔案
                msg = GET_NEW_FILE;
                break;
              default:
                break;
            }
            return msg;
          };

          Vue.notification.show(
            "Asus Chat",
            {
              body: generateMessage(data),
            },
            {},
          );
        }
        if (type == "Text") {
          commit("removeOfflineMessage", data.uuid);
        }
        // 有新訊息提示
        commit("toggleNewMsgAlert", !isScrollBottom);
        commit("updateMessage", data);
      } else {
        // AP-3752 isNowAgent 非當前Agent不做任何事
        return;
      }
    });
    // on ReadMessage
    state.connection.on("ReadMessage", function (data) {
      // console.log("你讀訊息了:", data);
      commit("readRoomMessage", data);
    });
    // on CustomerRead
    state.connection.on("CustomerRead", function (data) {
      const { clientChatRoomId, clientChatMessageId, custReadTitme } = data;
      state.clientChatRoomData.forEach((el, i) => {
        if (el.id == clientChatRoomId) {
          state.clientChatRoomData[i].messages.forEach((m, mi) => {
            if (m.id == clientChatMessageId) {
              state.clientChatRoomData[i].messages[mi].custReadTitme =
                custReadTitme;
              return;
            }
          });
        }
      });
    });
    // unsendMessage
    state.connection.on("unsendMessage", function (data) {
      commit("removeMessage", data);
    });
    state.connection.on("LeaveRoom", function (data) {
      // data : {clientChatRoomId: 'xxxx'}
      // console.log("(on)Agent LeaveRoom", data);
      let room = state.clientChatRoomData.find(
        (r) => r.id == data.clientChatRoomId,
      );

      // agent 不能結束對話, 等候 client 端選擇確認離開才可結束
      commit("finishClientChatRoom", data);
      // start pickup countdown
      let timer;
      if (room.leftPickUpBufferTime > 0) {
        // console.log('handlePickUp');
        timer = setInterval(function () {
          if (room.leftPickUpBufferTime > 0) {
            commit("handlePickUp", room.id);
          } else {
            clearInterval(timer);
          }
        }, 1000);
      }
    });
    state.connection.on("ChatFinish", function (data) {
      // client 端發起
      // console.log("ChatFinish", data);
      let room = state.clientChatRoomData.find(
        (r) => r.id == data.clientChatRoomId,
      );
      commit("finishClientChatRoom", data);
      // start pickup countdown
      let timer;
      if (room.leftPickUpBufferTime > 0) {
        // console.log('handlePickUp');
        timer = setInterval(function () {
          if (room.leftPickUpBufferTime > 0) {
            commit("handlePickUp", room.id);
          } else {
            clearInterval(timer);
          }
        }, 1000);
      }
    });
    state.connection.on("ClientChatSurveyResult", function (data) {
      commit("updateSurveyResult", data);
    });
    state.connection.on("RemoveLeaveRoomUser", function (data) {
      // console.log("(on)RemoveLeaveRoomUser", data);
    });
    state.connection.on("ReceiveCustomerHeartBeat", function (data) {
      commit("saveCustomerHeartBeat", data);
    });
    state.connection.on("AssignTransferResult", function (data) {
      // console.log("(on)AssignTransferResult", data);
    });
    state.connection.on("logout", function (data) {
      // console.log("偵聽到 on logout 事件", data);
      dispatch("stopConnection");
      Message({
        message: MSG_ONLOGOUT,
        center: true,
        iconClass: "el-icon-warning",
        customClass: "msg msg-warning",
        showClose: true,
        duration: 0,
      });
      commit("account/cleanUser", null, { root: true });
      router.push("/login");
    });
    state.connection.on("agentOnline", function () {});
    // webrtc
    state.connection.on("callResult", function (data) {
      // console.log(`[C]Client ${data.isAcceptCall ? 'accept' : 'reject'} invitation.`);
      let { isAcceptCall, clientChatRoomId } = data;
      // {clientChatRoomId: '20801', isAcceptCall: false}
      if (isAcceptCall) {
        dispatch("webrtc/initLocalStream", clientChatRoomId, { root: true });
      } else {
        Message({
          message: CLIENT_REFUSES,
          center: true,
          iconClass: "el-icon-warning",
          customClass: "msg msg-warning",
          showClose: true,
          duration: 5000,
        });
      }
    });
    state.connection.on("receiveSignal", function (data) {
      dispatch("webrtc/receiveSignal", data, { root: true });
    });
    state.connection.on("callEnded", function (data) {
      // console.log('on callEnded 收到結束視訊!', data);
      let { hangUpBy } = data;
      if (hangUpBy == "Customer") {
        dispatch("webrtc/hangup", null, { root: true });
        Message({
          message: CLIENT_HANGUP,
          center: true,
          iconClass: "el-icon-warning",
          customClass: "msg msg-warning",
          showClose: true,
          duration: 5000,
        });
      }
    });
  },
  // invoke
  sendTypingToCustomer({ state }, payload) {
    if (!state.connection) return;
    let status =
      state.connection.state ||
      state.connection._connectionState ||
      state.connection.connectionState;
    if (status !== "Connected") return;
    // console.log('sendTypingToCustomer', payload);
    state.connection.invoke("sendTypingToCustomer", payload);
  },
  async sendNewMessage({ state, commit, dispatch }, payload) {
    let status = state.connection._connectionState;
    let internet = state.internetState;
    let uuid = new Date().getTime().toString();
    let data = {
      replyId: state.replyMessage ? state.replyMessage.id : null,
      uuid: uuid,
      status: "0",
      ...payload,
    };
    if (internet == "offline" || status !== "Connected") {
      setTimeout(() => {
        commit("setOfflineMessage", data); // 暫存區
      }, 500);
      return false;
    }
    dispatch("checkIdle", payload);
    commit("setReplyMessage", null); // clean replyMessage
    try {
      await state.connection.invoke("SendMessageToCustomer", data);
      return true;
    } catch (error) {
      // AP-12932 送出當下才失敗的訊息也要進佇列，否則會直接消失
      commit("setOfflineMessage", data);
      return false;
    }
  },
  checkIdle({ rootState, state, dispatch }, payload) {
    const { BasicSetting_AgentIdleMins, BasicSetting_IsAgentIdleMins } =
      rootState.account.AC_config;
    if (BasicSetting_AgentIdleMins == "0") {
      // BasicSetting_AgentIdleMins = 0 代表不啟用
      return;
    }

    const agentId = rootState.account.AC_userdata.id;
    const clientChatRoomId = payload.clientChatRoomId;
    const room = state.clientChatRoomData.find((room) => {
      return room.id == clientChatRoomId;
    });
    if (!room) return;
    const filterMessages = room.messages.filter((msg) => {
      return msg.type !== "System";
    });
    const lastestMessage = filterMessages[filterMessages.length - 1];
    const isCustomer = lastestMessage.user.id !== agentId;
    let lastestReplyTime = lastestMessage
      ? new Date(lastestMessage.createAt).getTime()
      : null;

    const timeZone = new Date().getTimezoneOffset();
    const now = new Date().getTime() + timeZone * 1000 * 60;
    const diffTime = Math.floor((now - lastestReplyTime) / 1000);

    if (BasicSetting_IsAgentIdleMins === "True") {
      // 只有上次發言是客戶才判斷
      if (isCustomer) {
        console.log("客戶上次發言", diffTime, "秒前");
        if (diffTime > BasicSetting_AgentIdleMins * 60) {
          console.log("距離上次客戶發言，超過閒置時間，紀錄一次閒置");
          dispatch("idleTrigger", clientChatRoomId);
        }
      }
    } else {
      // 不判斷，看上一句就對了
      console.log(isCustomer ? "客戶" : "代理", "上次發言", diffTime, "秒前");
      if (diffTime > BasicSetting_AgentIdleMins * 60) {
        console.log("距離上次訊息超過閒置時間，紀錄一次閒置");
        dispatch("idleTrigger", clientChatRoomId);
      }
    }
  },
  async idleTrigger({ state }, payload) {
    try {
      await ClientChat.AgentIdleTrigger({
        ClientChatRoomId: payload,
      });
    } catch (error) {
      console.log("catch", error);
    }
  },
  async sendOfflineMessage({ state, commit, dispatch }, payload) {
    // 離線未送出的訊息
    let target = state.offlineMessages.find((m) => m.uuid == payload);
    if (!target) return false;
    const sent = await dispatch("sendNewMessage", target);
    if (sent) {
      commit("removeOfflineMessage", payload);
    }
    return sent;
  },

  // AP-12932 依佇列順序逐筆補送，前一筆成功才送下一筆
  async flushOfflineMessages({ state, dispatch }, attempt = 0) {
    if (flushing) return;
    clearTimeout(flushTimer);
    if (!state.offlineMessages.length) return;

    flushing = true;
    let failed = false;
    try {
      for (const message of [...state.offlineMessages]) {
        let sent = false;
        try {
          sent = await dispatch("sendOfflineMessage", message.uuid);
        } catch (error) {
          console.log("flushOfflineMessages failed", error);
        }
        if (!sent) {
          failed = true;
          break; // 保留順序，不跳過失敗的那筆繼續送後面的
        }
      }
    } finally {
      flushing = false;
    }

    if (!failed) return;
    if (attempt + 1 >= RETRY_DELAYS.length) return; // 放棄，留給手動重送 UI
    flushTimer = setTimeout(() => {
      dispatch("flushOfflineMessages", attempt + 1);
    }, retryDelay(attempt));
  },
  readMessage({ state }, payload) {
    let room = state.clientChatRoomData.find((room) => {
      return room.id == payload.ClientChatRoomId;
    });
    let isFinished = room.isFinished;
    // console.log("isFinished", isFinished);
    if (state.connection.state == "Connected" && !isFinished) {
      state.connection.invoke("AgentReadMessage", payload);
    }
  },
  leaveRoom({ state }, payload) {
    // console.log('(i)AgentLeaveRoom payload');
    state.connection.invoke("AgentLeaveRoom", payload);
  },
  unsendNewMessage({ state }, payload) {
    state.connection.invoke("AgentUnSendMessage", payload);
  },
  pickUp({ commit }, payload) {
    state.connection.invoke("AgentPickUp", payload);
    commit("cleanBufferTime", payload.ClientChatRoomId);
  },
  // transfer
  agentAutoTransfer({ state }, payload) {
    return new Promise((resolve, reject) => {
      state.connection
        .invoke("AgentAutoTransfer", payload)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  agentAssignTransfer({ state }, payload) {
    //console.log("agentAssignTransfer ", payload);
    return new Promise((resolve, reject) => {
      state.connection
        .invoke("AgentAssignTransfer", payload)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
          // console.log("AgentAssignTransfer error", err);
        });
    });
  },
  // webrtc
  callCustomer({ state }, payload) {
    console.log(
      `[A]Agent send video invitation to room ${payload.ClientChatRoomId}.`,
    );
    state.connection.invoke("callCustomer", payload);
  },
  hangup({ state, dispatch }, payload) {
    state.connection.invoke("hangUpByAgent", payload);
    dispatch("webrtc/hangup", null, { root: true });
  },
  agentTriggerIdle({ state }, payload) {
    state.connection.invoke("AgentTriggerIdle", payload); // 專員強制觸發閒置
  },
  // AP-12933 已結束超過保留分鐘數的聊天室自列表移除，與重新整理後後端撈回的範圍一致
  removeExpiredClientChatRoom({ state, rootState, commit }) {
    const { isChatListFilterByFinishTime, chatListFinishedKeepMinutes } =
      rootState.account.AC_userdata;
    const keepMinutes = Number(chatListFinishedKeepMinutes);
    if (!isChatListFilterByFinishTime || !(keepMinutes > 0)) return;

    const { name, params } = router.currentRoute;
    const openRoomId = name === "client" ? params.id : null;
    const expireTime = dayjs.utc().subtract(keepMinutes, "minute");

    const expiredIds = state.clientChatRoomData
      .filter((room) => {
        return (
          room.isFinished &&
          room.finishDateTime &&
          // 正在查看的聊天室、接手倒數中的聊天室先保留
          room.id != openRoomId &&
          !(room.leftPickUpBufferTime > 0) &&
          dayjs.utc(room.finishDateTime).isBefore(expireTime)
        );
      })
      .map((room) => room.id);

    if (expiredIds.length) {
      commit("removeClientChatRooms", expiredIds);
    }
  },
};

const mutations = {
  setInternetState(state, payload) {
    state.internetState = payload;
  },
  setLoading(state, payload) {
    state.loading = payload;
  },
  cleanConnection(state, payload) {
    state.connection = null;
    if (payload) {
      state.clientChatRoomData = [];
    }
  },
  setClientChatRoom(state, payload) {
    state.clientChatRoomData = payload;
  },
  setClientChatRoomStatus(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.roomId) {
        state.clientChatRoomData[i].customerStatus = payload.content;
      }
    });
  },
  setClientChatRoomIsNowAgent(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload) {
        state.clientChatRoomData[i].isNowAgent = false;
      }
    });
  },
  addNewClientChatRoom(state, payload) {
    payload.heartBeatTime = new Date().getTime();
    state.clientChatRoomData.unshift(payload);
  },
  updateMessage(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.roomId) {
        if (state.clientChatRoomData[i].messages == null) {
          state.clientChatRoomData[i].messages = [];
        }
        state.clientChatRoomData[i].messages.push(payload);
      }
    });
  },
  updateSurveyResult(state, payload) {
    let room = state.clientChatRoomData.find((room) => {
      return room.id == payload.clientChatRoomId;
    });

    room.isSatisfied = payload.isSatisfied;
  },
  setTempMessage(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.roomId) {
        state.clientChatRoomData[i].tempMessage = payload;
      }
    });
  },
  removeMessage(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.roomId) {
        state.clientChatRoomData[i].messages.forEach((m, mi) => {
          if (m.id == payload.messageId) {
            state.clientChatRoomData[i].messages[mi].isHidden = true;
            return;
          }
        });
        return;
      }
    });
  },
  readRoomMessage(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.roomId) {
        state.clientChatRoomData[i].messages.forEach((m) => {
          m.isRead = true;
          return;
        });
        return;
      }
    });
  },
  finishClientChatRoom(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.clientChatRoomId) {
        state.clientChatRoomData[i].isFinished = true;
        state.clientChatRoomData[i].heartBeatTime = null;
        // AP-12933 ChatFinish / LeaveRoom 未帶結束時間，以收到當下的 UTC 時間補上
        if (!state.clientChatRoomData[i].finishDateTime) {
          state.clientChatRoomData[i].finishDateTime = dayjs
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss");
        }
      }
    });
  },
  removeClientChatRooms(state, ids) {
    state.clientChatRoomData = state.clientChatRoomData.filter(
      (room) => !ids.includes(room.id),
    );
  },
  handlePickUp(state, roomId) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == roomId) {
        state.clientChatRoomData[i].leftPickUpBufferTime--;
      }
    });
  },
  cleanBufferTime(state, roomId) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == roomId) {
        state.clientChatRoomData[i].leftPickUpBufferTime = 0;
      }
    });
  },
  updateRoomCustomer(state, payload) {
    let { id, product, productSN } = payload;
    state.clientChatRoomData.forEach((el, i) => {
      if (el.customerId == id) {
        state.clientChatRoomData[i].customerInfo.product = product;
        state.clientChatRoomData[i].customerInfo.productSN = productSN;
      }
    });
  },
  toggleNewMsgAlert(state, payload) {
    state.hasNewMsg = payload;
  },
  setOfflineMessage(state, payload) {
    const room = state.clientChatRoomData.find(
      (room) => room.id == payload.clientChatRoomId,
    );
    const findIndex = room
      ? room.messages.findIndex((msg) => msg.uuid == payload.uuid)
      : -1;
    // AP-12932 補送失敗會再次進到這裡，已在佇列中就不重複堆入
    const queued = state.offlineMessages.some((m) => m.uuid == payload.uuid);

    if (findIndex == -1 && !queued) {
      // 若找不到這筆，才加入緩存區
      let tempMessage = {
        ...payload,
        // AP-12932 uuid 必須跨重試維持不變，後端才能以 Uuid 去重
        isTemp: true,
        isSendFail: true,
        sendFailTime: dayjs().utc().format("YYYY-MM-DD HH:mm:ss"),
      };
      state.offlineMessages.push(tempMessage);
      saveOfflineMessages(state.offlineMessages);
    }
  },
  cleanOfflineMessages(state) {
    state.offlineMessages = [];
    saveOfflineMessages(state.offlineMessages);
  },
  removeOfflineMessage(state, payload) {
    let target = state.offlineMessages.findIndex((m) => m.uuid == payload);
    if (target !== -1) {
      state.offlineMessages.splice(target, 1);
      saveOfflineMessages(state.offlineMessages);
    }
  },
  saveCustomerHeartBeat(state, payload) {
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == payload.clientChatRoomId) {
        state.clientChatRoomData[i].heartBeatTime = Date.now();
      }
    });
  },
  updateRoomChatCaseId(state, payload) {
    const { roomId, clientChatCaseId } = payload;
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == roomId) {
        state.clientChatRoomData[i].clientChatCaseId = clientChatCaseId;
      }
    });
  },
  updateRoomChatCaseNo(state, payload) {
    const { roomId } = payload;
    state.clientChatRoomData.forEach((el, i) => {
      if (el.id == roomId) {
        // 2025.01.01 非草稿時更新CaseNo(true)狀態提供辨識, 取代原本的 reload
        state.clientChatRoomData[i].clientChatCaseNo = true;
      }
    });
  },
  setReplyMessage(state, payload) {
    state.replyMessage = payload;
  },
};

const getters = {
  isChatting: (state) => {
    // 是否存在正在進行的聊天室
    const rooms = state.clientChatRoomData;
    const chattingRooms = rooms.filter((room) => {
      return !room.isFinished;
    }).length;
    return chattingRooms ? true : false;
  },
  connectionState: (state) => state.connection._connectionState,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
